export const MONTH_NAMES = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
export function spectralPeriodKind(preset) {
    return preset === 'all' ? 'year' : preset === 'custom' ? 'custom' : 'month';
}
export function spectralPeriodSummaryTitle(preset) {
    if (preset === 'all')
        return 'Итог за сезон';
    if (preset === 'custom')
        return 'Итог за период';
    // A preset month's card already displays its summary.
    return null;
}
export function spectralLegendTitle(legend) {
    var _a, _b;
    const formatDate = (value) => `${value.slice(8, 10)}.${value.slice(5, 7)}.${value.slice(0, 4)}`;
    const period = legend.dateTo
        ? `${legend.date.slice(0, 7) === legend.dateTo.slice(0, 7) ? legend.date.slice(8, 10) : formatDate(legend.date)}–${formatDate(legend.dateTo)}`
        : formatDate(legend.date);
    return `${legend.type.toUpperCase()} · ${period} · ${legend.dateTo ? 'ср.' : 'индекс'} ${(_b = (_a = legend.weightedIndex) === null || _a === void 0 ? void 0 : _a.toFixed(2)) !== null && _b !== void 0 ? _b : 'нет данных'}`;
}
export function spectralEmptyMessage(statuses) {
    if (statuses === null || statuses === void 0 ? void 0 : statuses.includes('stale'))
        return 'Старые снимки сохранены, но скрыты до проверки облачности и качества. Запустите пересчёт сезона в разделе «Снимки».';
    if (statuses === null || statuses === void 0 ? void 0 : statuses.some((status) => status === 'pending' || status === 'failed'))
        return 'Снимки ещё не обработаны или не удалось завершить загрузку. Проверьте состояние сезона в разделе «Снимки».';
    if (statuses === null || statuses === void 0 ? void 0 : statuses.includes('rejected'))
        return 'Снимки выбранного периода не прошли проверку облачности или качества и не входят в средние значения.';
    if (statuses !== null && statuses.every((status) => status === 'empty'))
        return 'В выбранном периоде снимков пока нет. Выберите другой период.';
    return 'В выбранном периоде нет пригодных снимков для расчёта. Пустые дни не считаются нулевыми значениями.';
}
export function isUsableSpectralSnapshot(snapshot) {
    return snapshot.status === 'covered' && snapshot.id !== null
        && typeof snapshot.mean_value === 'number' && Number.isFinite(snapshot.mean_value);
}
export function spectralPeriodRange(season, preset, begin, end) {
    if (preset === 'custom')
        return [begin, end];
    if (preset === 'all')
        return [`${season}-01-01`, `${season}-12-31`];
    const month = Number(preset.slice('month-'.length));
    const prefix = `${season}-${String(month).padStart(2, '0')}`;
    const lastDay = new Date(Date.UTC(season, month, 0)).getUTCDate();
    return [`${prefix}-01`, `${prefix}-${String(lastDay).padStart(2, '0')}`];
}
export function isSpectralPeriodValid(range, season) {
    return range[0] <= range[1] && range.every((value) => {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || Number(value.slice(0, 4)) !== season)
            return false;
        const date = new Date(`${value}T00:00:00Z`);
        return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
    });
}
export function monthlyChartPoints(months) {
    return months.map((month) => ({
        date: month.date_from,
        date_to: month.date_to,
        snapshot_count: month.snapshot_count,
        index: month.snapshot_count > 0 && typeof month.mean_value === 'number' && Number.isFinite(month.mean_value) ? month.mean_value : null,
    }));
}
export function usableMonthlyEntries(months) {
    return months.filter((month) => month.snapshot_count > 0
        && typeof month.mean_value === 'number' && Number.isFinite(month.mean_value))
        .sort((first, second) => first.month.localeCompare(second.month));
}
// Null slots preserve calendar spacing and prevent a line from crossing a month
// without observations. They are not displayed as selectable months or ticks.
export function monthlyGraphPoints(months) {
    const usable = usableMonthlyEntries(months);
    if (usable.length === 0)
        return [];
    const source = new Map(months.map((month) => [month.month, month]));
    const first = Number(usable[0].month.slice(0, 4)) * 12 + Number(usable[0].month.slice(5, 7)) - 1;
    const last = Number(usable[usable.length - 1].month.slice(0, 4)) * 12 + Number(usable[usable.length - 1].month.slice(5, 7)) - 1;
    const result = [];
    for (let monthIndex = first; monthIndex <= last; monthIndex += 1) {
        const year = Math.floor(monthIndex / 12);
        const month = monthIndex % 12 + 1;
        const key = `${year}-${String(month).padStart(2, '0')}`;
        const entry = source.get(key);
        result.push(entry
            ? monthlyChartPoints([entry])[0]
            : { date: `${key}-01`, date_to: `${key}-${String(new Date(Date.UTC(year, month, 0)).getUTCDate()).padStart(2, '0')}`, snapshot_count: 0, index: null });
    }
    return result;
}
export function spectralPanelLayout(mode, presentation, preset, monthCount, panelWidth, showSummary) {
    const cards = mode === 'monthly' && presentation === 'grid';
    // Match the card grid's width breakpoints. Never feed measured content
    // heights back into the panel: its available height must not affect itself.
    const columns = panelWidth <= 650 ? 1 : panelWidth <= 850 ? 2 : 3;
    const rows = Math.ceil(monthCount / Math.min(Math.max(1, monthCount), columns));
    const leftHeight = (preset === 'custom' ? 216 : 112) + 8 + 42;
    const contentHeight = cards ? Math.max(54, rows * 54 + Math.max(0, rows - 1) * 8) : mode === 'monthly' ? 152 : 220;
    const rightHeight = 34 + contentHeight + 26 + (showSummary ? 48 : 0);
    // The timeline is a separate full-width row beneath BOTH columns.
    const timelineHeight = mode !== 'monthly' || monthCount > 1 ? 44 : 0;
    const naturalHeight = 38 + Math.max(leftHeight, rightHeight) + timelineHeight + 4;
    const height = Math.min(480, Math.max(190, Math.ceil(naturalHeight)));
    return { height, maxHeight: Math.min(560, Math.max(320, height + 96)) };
}
export function preferredMonthIndex(months) {
    for (let index = months.length - 1; index >= 0; index -= 1) {
        const month = months[index];
        if (month.snapshot_count > 0 && typeof month.mean_value === 'number' && Number.isFinite(month.mean_value))
            return index;
    }
    return Math.max(0, months.length - 1);
}
